<template>
  <section class="dp-layout dsc-page">
    <section class="dp-left">
      <DepartmentPane directory-scope="school" @tree-node-click="treeNodeClick" :type="1" />
    </section>

    <section class="dp-right">
      <toolbar :buttonList="buttonList" @callFunction="callFunction" :searchList="[]" />

      <div class="dsc-shell">
        <div v-if="loading" class="dsc-state">
          <i class="el-icon-loading dsc-state__icon is-spin" />
          <p>正在加载配置…</p>
        </div>

        <div v-else-if="DepartmentId > 0" class="dsc-body">
          <header class="dsc-hero">
            <h2 class="dsc-title">学校配置</h2>
            <el-button type="primary" icon="el-icon-check" :loading="saving" @click="saveConfig">保存配置</el-button>
          </header>

          <el-tabs v-model="activeConfigTab" class="dsc-config-tabs" :before-leave="beforeConfigTabLeave">
            <el-tab-pane label="放学流程" name="flow">
              <div class="dsc-tiles">
                <article class="dsc-tile">
                  <div class="dsc-tile__title">
                    <i class="el-icon-message" />
                    <span>家长钉钉通知</span>
                  </div>
                  <div class="dsc-tile__body">
                    <span class="dsc-k">放学与滞留推送</span>
                    <el-switch v-model="form.parentDingNotifyEnabled" active-text="开启" inactive-text="关闭" />
                  </div>
                  <p class="dsc-desc">开启后，班牌或教师发起放学时，将向家长钉钉推送「放学通知」；若有学生滞留，将额外推送「滞留通知」。</p>
                </article>

                <article class="dsc-tile">
                  <div class="dsc-tile__title">
                    <i class="el-icon-headset" />
                    <span>放学语音播报</span>
                  </div>
                  <div class="dsc-tile__body dsc-tile__body--stack">
                    <div class="dsc-inline">
                      <span class="dsc-k">播报次数</span>
                      <el-input-number v-model="form.voiceRepeatCount" :min="1" :max="5" :step="1" size="small" controls-position="right" />
                      <span class="dsc-unit">次</span>
                    </div>
                    <div class="dsc-voice">
                      <span class="dsc-k">播报文案</span>
                      <span class="dsc-voice__prefix">班级名称 +</span>
                      <el-input v-model="form.voiceSuffix" placeholder="正在放学，请家长在等候区等待" size="small" clearable />
                    </div>
                  </div>
                  <p class="dsc-desc">播报内容 = 班级名称 + 文案后缀，例如「一年级1班正在放学，请家长在等候区等待」。文案清空后重置为默认内容。</p>
                </article>

                <article class="dsc-tile">
                  <div class="dsc-tile__title">
                    <i class="el-icon-magic-stick" />
                    <span>自动放学触发</span>
                  </div>
                  <div class="dsc-tile__body dsc-tile__body--stack">
                    <div class="dsc-inline">
                      <span class="dsc-k">自动触发</span>
                      <el-switch v-model="form.autoTriggerEnabled" active-text="开启" inactive-text="关闭" />
                    </div>
                    <div class="dsc-inline">
                      <span class="dsc-k">触发所需打卡人数</span>
                      <el-input-number
                        v-model="form.autoTriggerThreshold"
                        :min="1"
                        :max="200"
                        :step="1"
                        size="small"
                        controls-position="right"
                        :disabled="!form.autoTriggerEnabled"
                      />
                      <span class="dsc-unit">人</span>
                    </div>
                  </div>
                  <p class="dsc-desc">开启后，在放学时段内当班级有效打卡人数达到阈值，系统将自动发起放学（等效教师手动操作）。按当前时段默认标签筛选学生，未配置标签则对全班生效。</p>
                </article>

                <article class="dsc-tile">
                  <div class="dsc-tile__title">
                    <i class="el-icon-data-board" />
                    <span>LED 叫号</span>
                  </div>
                  <div class="dsc-tile__body dsc-tile__body--stack">
                    <div class="dsc-inline">
                      <span class="dsc-k">校门 LED 叫号</span>
                      <el-switch v-model="form.ledNotifyEnabled" active-text="开启" inactive-text="关闭" />
                    </div>
                    <div class="dsc-inline">
                      <span class="dsc-k">每遍之间的额外停顿</span>
                      <el-input-number
                        v-model="form.ledVoiceIntervalSeconds"
                        :min="2"
                        :max="30"
                        :step="1"
                        size="small"
                        controls-position="right"
                        :disabled="!form.ledNotifyEnabled"
                      />
                      <span class="dsc-unit">秒</span>
                    </div>
                    <div class="dsc-led-timing">
                      <i class="el-icon-time" />
                      <span>
                        例：「{{ ledTiming.text }}」共 {{ ledTiming.len }} 字，每遍约播
                        <b>{{ ledTiming.voiceSec }}</b> 秒，再停顿 <b>{{ ledTiming.interval }}</b> 秒；
                        播 {{ ledTiming.repeat }} 遍，每个班在 LED 上约停留 <b>{{ ledTiming.totalSec }}</b> 秒。
                      </span>
                    </div>
                  </div>
                  <p class="dsc-desc">播报时长按字数估算：每秒约 3.2 字，最少 3.5 秒；实际语速以设备为准，前后两遍若有打断可调大停顿。<br />开启后，手动或自动放学成功时，向该班所在校区已启用的校门 LED 下发「班级名称 + 播报文案」，按「放学语音播报」的次数重复播报；同一校区多个班依次排队。设备在「校门LED设备」中维护。</p>
                </article>
              </div>
            </el-tab-pane>

            <el-tab-pane label="违停登记" name="violation">
              <div class="dsc-tiles dsc-tiles--violation">
                <article class="dsc-tile">
                  <div class="dsc-tile__title">
                    <i class="el-icon-s-check" />
                    <span>违停登记审核</span>
                  </div>
                  <div class="dsc-tile__body">
                    <span class="dsc-k">教师提交的违停</span>
                    <el-radio-group v-model="form.needApproval" size="small">
                      <el-radio-button :label="true">需要审核</el-radio-button>
                      <el-radio-button :label="false">不需要审核</el-radio-button>
                    </el-radio-group>
                  </div>
                  <p class="dsc-desc">开启后，教师提交的违停登记需经审核人通过后才在大屏展示。后台提交一律免审。</p>
                </article>

                <article class="dsc-tile">
                  <div class="dsc-tile__title">
                    <i class="el-icon-tickets" />
                    <span>关联扣分</span>
                  </div>
                  <div class="dsc-tile__body dsc-tile__body--stack">
                    <div class="dsc-field-block">
                      <span class="dsc-k">点评扣分项</span>
                      <el-cascader
                        v-model="form.violationEvalItemId"
                        :options="evalTree"
                        :props="cascaderProps"
                        :show-all-levels="true"
                        clearable
                        filterable
                        placeholder="待改进项，可清空"
                        size="small"
                        class="dsc-full"
                        @change="onEvalItemChange"
                      />
                      <p v-if="form.violationEvalItemInvalid" class="dsc-warn">配置已失效，请重新选择</p>
                      <div v-if="evalScoreIsMax" class="dsc-inline dsc-score">
                        <el-input-number
                          v-model="form.violationEvalDeductScore"
                          :min="evalScoreMin"
                          :max="evalItemScoreMax"
                          :step="1"
                          :precision="2"
                          size="small"
                          controls-position="right"
                        />
                        <span class="dsc-unit">分，上限 {{ formatScore(evalItemScoreMax) }}</span>
                      </div>
                    </div>
                    <div class="dsc-inline">
                      <span class="dsc-k">教师端显示扣分数</span>
                      <el-switch v-model="form.violationShowDeductScore" active-text="显示" inactive-text="不显示" />
                    </div>
                  </div>
                  <p class="dsc-desc">点评项全校统一；未选则只登记、不上德育账。固定分按模板扣；上限项需另填实际分。关闭「显示扣分数」后，手机端只显示扣分项名称。值日扣分项请在下方按校区分别配置。</p>
                </article>

                <article class="dsc-tile dsc-tile--full">
                  <div class="dsc-tile__title">
                    <i class="el-icon-monitor" />
                    <span>上屏规则</span>
                  </div>
                  <div class="dsc-tile__body dsc-tile__body--stack">
                    <div class="dsc-field-block">
                      <span class="dsc-k">投放校区</span>
                      <el-tabs
                        v-if="campusOptions.length"
                        v-model="scopeCampusTab"
                        type="card"
                        class="dsc-campus-tabs"
                        :before-leave="beforeScopeCampusLeave"
                      >
                        <el-tab-pane
                          v-for="c in campusOptions"
                          :key="c.Id"
                          :label="c.Name"
                          :name="String(c.Id)"
                        />
                      </el-tabs>
                      <p v-else class="dsc-warn">暂无校区，请先在组织架构中维护校区</p>
                    </div>
                    <div class="dsc-field-block">
                      <span class="dsc-k">值日扣分项</span>
                      <el-cascader
                        v-model="form.violationDutyItemId"
                        :options="dutyTree"
                        :props="cascaderProps"
                        :show-all-levels="true"
                        clearable
                        filterable
                        :disabled="!scopeCampusId"
                        placeholder="当前校区模板扣分项，可清空"
                        size="small"
                        class="dsc-full"
                        @change="form.violationDutyItemInvalid = false"
                      />
                      <p v-if="form.violationDutyItemInvalid" class="dsc-warn">配置已失效，请重新选择</p>
                    </div>
                    <div class="dsc-field-block">
                      <span class="dsc-k">投放范围</span>
                      <div class="dsc-inline">
                        <el-radio-group v-model="form.violationPublishAllScreens" size="small" :disabled="!scopeCampusId">
                          <el-radio-button :label="true">该校区全部大屏</el-radio-button>
                          <el-radio-button :label="false">指定大屏</el-radio-button>
                        </el-radio-group>
                      </div>
                      <el-select
                        v-if="scopeCampusId && !form.violationPublishAllScreens"
                        v-model="form.violationScreenIdList"
                        multiple
                        filterable
                        placeholder="请选择大屏"
                        size="small"
                        class="dsc-full dsc-select-mt"
                      >
                        <el-option
                          v-for="s in screenOptions"
                          :key="s.Id"
                          :label="s.ScreenName"
                          :value="String(s.Id)"
                        />
                      </el-select>
                    </div>
                    <div class="dsc-field-block">
                      <span class="dsc-k">内容分屏</span>
                      <div class="dsc-inline">
                        <el-radio-group v-model="form.violationLayoutMode" size="small" :disabled="!scopeCampusId">
                          <el-radio-button :label="1">全屏</el-radio-button>
                          <el-radio-button :label="2">二分屏</el-radio-button>
                          <el-radio-button :label="4">四分屏</el-radio-button>
                        </el-radio-group>
                        <violation-layout-preview :mode="form.violationLayoutMode" />
                      </div>
                    </div>
                    <div class="dsc-field-block">
                      <span class="dsc-k">展示时长</span>
                      <div class="dsc-inline">
                        <el-radio-group v-model="form.violationDisplayPreset" size="small" :disabled="!scopeCampusId">
                          <el-radio-button label="day">1 天</el-radio-button>
                          <el-radio-button label="week">1 周</el-radio-button>
                          <el-radio-button label="month">1 个月</el-radio-button>
                          <el-radio-button label="custom">自定义</el-radio-button>
                        </el-radio-group>
                        <template v-if="form.violationDisplayPreset === 'custom'">
                          <el-input-number
                            v-model="form.violationDisplayDays"
                            :min="1"
                            :max="365"
                            :step="1"
                            size="small"
                            controls-position="right"
                            :disabled="!scopeCampusId"
                          />
                          <span class="dsc-unit">天</span>
                        </template>
                      </div>
                    </div>
                  </div>
                  <p class="dsc-desc">按校区分别配置值日扣分项、投放范围、分屏与展示时长。该校区全部大屏=仅投到绑定年级属于该校区的大屏；指定大屏=只投勾选屏。未绑年级的大屏视为全校，可播各校区内容。手机端只问是否投放；时长从免审提交或审核通过时起算。每块大屏可在设备管理里单独覆盖分屏。</p>
                </article>
              </div>
            </el-tab-pane>
          </el-tabs>
        </div>

        <div v-else class="dsc-state">
          <i class="el-icon-office-building dsc-state__icon" />
          <p>请在左侧选择学校 / 机构</p>
        </div>
      </div>
    </section>
  </section>
</template>

<script>
import {
  getDismissalScreenConfig,
  setDismissalScreenConfig,
  getViolationEvalDeductTree,
  getViolationDutyDeductTree,
  getScreenList,
  parseScreenListResponse
} from "@/api/dismissal";
import { getCampusList } from "../../api/modules/misc";
import Toolbar from "../../components/Toolbar";
import DepartmentPane from "../../components/DepartmentPane";
import ViolationLayoutPreview from "./ViolationLayoutPreview.vue";
import { getButtonList } from "../../promissionRouter";
import { toDeptId, treeNodeDeptId } from '../../utils/deptCascaderProps';

export default {
  name: "DismissalScreenConfig",
  components: { Toolbar, DepartmentPane, ViolationLayoutPreview },
  data() {
    return {
      buttonList: [],
      DepartmentId: 0,
      activeConfigTab: 'flow',
      loading: false,
      saving: false,
      form: {
        needApproval: false,
        /** 家长钉钉放学/滞留推送，与后端 DismissalParentDingNotifyMode 对应，默认开启 */
        parentDingNotifyEnabled: true,
        /** 语音播报次数，默认 2 次 */
        voiceRepeatCount: 2,
        /** 语音播报文案后缀 */
        voiceSuffix: "正在放学，请家长在等候区等待",
        /** 自动放学触发开关，默认关闭 */
        autoTriggerEnabled: false,
        /** 自动放学触发所需最低打卡人数 */
        autoTriggerThreshold: 1,
        /** 校门 LED 叫号开关，默认关闭 */
        ledNotifyEnabled: false,
        /** 校门 LED 每遍之间的额外停顿秒数（2~30），默认 5 */
        ledVoiceIntervalSeconds: 5,
        violationEvalItemId: null,
        violationEvalItemInvalid: false,
        violationEvalScoreMode: "",
        violationEvalItemScore: 0,
        violationEvalDeductScore: null,
        violationDutyItemId: null,
        violationDutyItemInvalid: false,
        violationShowDeductScore: true,
        violationPublishAllScreens: true,
        violationScreenIdList: [],
        violationLayoutMode: 1,
        violationDisplayPreset: "day",
        violationDisplayDays: 1
      },
      evalTree: [],
      dutyTree: [],
      screenOptions: [],
      campusOptions: [],
      /** 当前编辑投放范围的校区 Tab（字符串 Id，供 el-tabs） */
      scopeCampusTab: "",
      /** 各校区投放/分屏/时长/值日扣分项草稿，保存时按校区提交 */
      campusScopeMap: {},
      /** 学校级旧值日项（兼容回填；新保存写入各校区 DutyItemId） */
      legacyDutyItemId: null,
      /** 学校级分屏/时长兜底（旧数据或未配校区时） */
      schoolDefaults: {
        layoutMode: 1,
        displayPreset: "day",
        displayDays: 1
      },
      cascaderProps: {
        value: "id",
        label: "name",
        children: "children",
        emitPath: false
      }
    };
  },
  computed: {
    /**
     * LED 叫号时长示例，与后端 DismissalLedHelper.EstimateVoiceDuration 算法一致。最后更新：2026-09-30
     */
    ledTiming() {
      const text = "一年级1班" + (this.form.voiceSuffix || "正在放学，请家长在等候区等待");
      const len = text.length;
      const voice = Math.max(3.5, len / 3.2);
      const interval = Number(this.form.ledVoiceIntervalSeconds) || 5;
      const repeat = Number(this.form.voiceRepeatCount) || 1;
      return {
        text,
        len,
        voiceSec: voice.toFixed(1),
        interval,
        repeat,
        totalSec: Math.round((voice + interval) * repeat),
      };
    },
    scopeCampusId() {
      const t = String(this.scopeCampusTab || "").trim();
      return t && t !== "0" ? t : "";
    },
    evalSelectedLeaf() {
      return this.findLeaf(this.evalTree, this.form.violationEvalItemId);
    },
    evalScoreIsMax() {
      const leaf = this.evalSelectedLeaf;
      const mode = leaf
        ? (leaf.scoreMode || leaf.ScoreMode)
        : this.form.violationEvalScoreMode;
      return String(mode || "").toLowerCase() === "max";
    },
    evalItemScoreMax() {
      const leaf = this.evalSelectedLeaf;
      const raw = leaf
        ? (leaf.scoreValue != null ? leaf.scoreValue : leaf.ScoreValue)
        : this.form.violationEvalItemScore;
      const n = Number(raw);
      return n > 0 ? n : 0;
    },
    evalScoreMin() {
      return this.evalItemScoreMax >= 1 ? 1 : 0.01;
    }
  },
  methods: {
    /** 切换配置 Tab 前回写违停校区草稿。最后更新：2026-08-31 */
    beforeConfigTabLeave(activeName, oldActiveName) {
      if (oldActiveName === 'violation') {
        this.flushCurrentCampusScope();
      }
      return true;
    },
    treeNodeClick(data) {
      const id = treeNodeDeptId(data);
      if (!id) return;
      this.DepartmentId = id;
      this.activeConfigTab = 'flow';
      if (this.DepartmentId) {
        this.loading = true;
        this.loadConfig();
      } else {
        this.form.needApproval = false;
        this.form.parentDingNotifyEnabled = true;
        this.form.voiceRepeatCount = 2;
        this.form.voiceSuffix = "正在放学，请家长在等候区等待";
        this.form.autoTriggerEnabled = false;
        this.form.autoTriggerThreshold = 1;
        this.form.ledNotifyEnabled = false;
        this.form.ledVoiceIntervalSeconds = 5;
        this.resetViolationForm();
      }
    },
    resetViolationForm() {
      this.form.violationEvalItemId = null;
      this.form.violationEvalItemInvalid = false;
      this.form.violationEvalScoreMode = "";
      this.form.violationEvalItemScore = 0;
      this.form.violationEvalDeductScore = null;
      this.form.violationDutyItemId = null;
      this.form.violationDutyItemInvalid = false;
      this.form.violationShowDeductScore = true;
      this.form.violationPublishAllScreens = true;
      this.form.violationScreenIdList = [];
      this.form.violationLayoutMode = 1;
      this.form.violationDisplayPreset = "day";
      this.form.violationDisplayDays = 1;
      this.scopeCampusTab = "";
      this.campusScopeMap = {};
      this.legacyDutyItemId = null;
      this.dutyTree = [];
      this.schoolDefaults = { layoutMode: 1, displayPreset: "day", displayDays: 1 };
      this.campusOptions = [];
    },
    findLeaf(nodes, id) {
      if (!id || !Array.isArray(nodes)) return null;
      const key = String(id);
      for (let i = 0; i < nodes.length; i++) {
        const n = nodes[i];
        if (String(n.id) === key && (!n.children || !n.children.length)) return n;
        const hit = this.findLeaf(n.children, id);
        if (hit) return hit;
      }
      return null;
    },
    formatScore(v) {
      const n = Number(v);
      if (Number.isNaN(n)) return "";
      return Number.isInteger(n) ? String(n) : String(n);
    },
    onEvalItemChange() {
      if (!this.evalScoreIsMax) {
        this.form.violationEvalDeductScore = null;
        this.form.violationEvalScoreMode = "";
        this.form.violationEvalItemScore = 0;
        return;
      }
      this.form.violationEvalScoreMode = "Max";
      this.form.violationEvalItemScore = this.evalItemScoreMax;
      const cur = Number(this.form.violationEvalDeductScore);
      if (!cur || cur > this.evalItemScoreMax) {
        this.form.violationEvalDeductScore = this.evalItemScoreMax;
      }
    },
    pick(obj, camel, pascal) {
      if (!obj) return undefined;
      return obj[camel] != null ? obj[camel] : obj[pascal];
    },
    normalizeTree(nodes) {
      if (!Array.isArray(nodes)) return [];
      return nodes.map(n => {
        const childrenRaw = n.children || n.Children;
        const isLeaf = n.isLeaf != null ? n.isLeaf : n.IsLeaf;
        const children = this.normalizeTree(childrenRaw);
        return {
          id: String(n.id != null ? n.id : n.Id),
          name: n.name != null ? n.name : n.Name,
          scoreMode: n.scoreMode || n.ScoreMode || "",
          scoreValue: n.scoreValue != null ? n.scoreValue : n.ScoreValue,
          children: isLeaf || !children.length ? undefined : children
        };
      });
    },
    loadTreesAndScreens() {
      if (!this.DepartmentId) return;
      getViolationEvalDeductTree({ departmentId: this.DepartmentId }).then(res => {
        const list = res && res.data && res.data.response ? res.data.response : [];
        this.evalTree = this.normalizeTree(list);
      }).catch(() => { this.evalTree = []; });
      getScreenList({ schId: this.DepartmentId, page: 1, intPageSize: 999 }).then(res => {
        this.screenOptions = parseScreenListResponse(res);
      }).catch(() => { this.screenOptions = []; });
      this.loadCampusOptions();
      this.loadDutyTreeForCampus(this.scopeCampusTab);
    },
    /** 按当前校区加载值日扣分树（校区优先，无一级类回退学校通用）。最后更新：2026-09-12 */
    loadDutyTreeForCampus(campusId) {
      if (!this.DepartmentId) {
        this.dutyTree = [];
        return;
      }
      const cid = String(campusId || "").trim();
      const params = { departmentId: this.DepartmentId };
      if (cid && cid !== "0") params.campusId = cid;
      getViolationDutyDeductTree(params).then(res => {
        // 异步返回时若已切到其它校区，丢弃过期结果
        if (String(this.scopeCampusTab || "") !== String(campusId || "")) return;
        const list = res && res.data && res.data.response ? res.data.response : [];
        this.dutyTree = this.normalizeTree(list);
      }).catch(() => {
        if (String(this.scopeCampusTab || "") !== String(campusId || "")) return;
        this.dutyTree = [];
      });
    },
    loadCampusOptions() {
      if (!this.DepartmentId) {
        this.campusOptions = [];
        return;
      }
      getCampusList({ page: 1, intPageSize: 999, departmentId: this.DepartmentId })
        .then(res => {
          this.campusOptions = (res && res.data && res.data.response && res.data.response.data) || [];
          if (!this.scopeCampusTab && this.campusOptions.length) {
            this.scopeCampusTab = String(this.campusOptions[0].Id);
            this.applyCampusScopeToForm(this.scopeCampusTab);
            this.loadDutyTreeForCampus(this.scopeCampusTab);
          } else if (this.scopeCampusTab) {
            this.loadDutyTreeForCampus(this.scopeCampusTab);
          }
        })
        .catch(() => {
          this.campusOptions = [];
        });
    },
    parseCampusScopesFromResponse(rawList) {
      const map = {};
      const list = Array.isArray(rawList) ? rawList : [];
      list.forEach(item => {
        if (!item) return;
        // 校区 Id 用字符串，避免雪花 long 经 Number 丢精度后多个校区撞 key
        const cid = String(this.pick(item, "campusId", "CampusId") || "").trim();
        if (!cid || cid === "0") return;
        const all = this.pick(item, "publishAllScreens", "PublishAllScreens");
        const ids = this.pick(item, "screenIds", "ScreenIds") || "";
        const layoutRaw = this.pick(item, "layoutMode", "LayoutMode");
        const layoutNum = Number(layoutRaw);
        const hasLayout = layoutNum === 1 || layoutNum === 2 || layoutNum === 4;
        const presetRaw = this.pick(item, "displayPreset", "DisplayPreset");
        const daysRaw = this.pick(item, "displayDays", "DisplayDays");
        const dutyIdRaw = this.pick(item, "dutyItemId", "DutyItemId");
        const dutyId = dutyIdRaw > 0 ? String(dutyIdRaw) : null;
        const dutyInvalid = !!this.pick(item, "dutyItemInvalid", "DutyItemInvalid");
        map[cid] = {
          PublishAllScreens: all === undefined || all === null ? true : !!all,
          ScreenIds: ids ? String(ids) : "",
          // 旧 JSON 无分屏/时长字段时留空，应用表单时回退到学校默认
          LayoutMode: hasLayout ? layoutNum : null,
          DisplayPreset: presetRaw ? String(presetRaw) : null,
          DisplayDays: daysRaw != null && Number(daysRaw) >= 1 ? Number(daysRaw) : null,
          DutyItemId: dutyId,
          DutyItemInvalid: dutyInvalid
        };
      });
      return map;
    },
    buildCampusScopeSnapshot() {
      const layout = Number(this.form.violationLayoutMode);
      const dutyId = this.form.violationDutyItemId;
      return {
        PublishAllScreens: !!this.form.violationPublishAllScreens,
        ScreenIds: this.form.violationPublishAllScreens
          ? ""
          : (this.form.violationScreenIdList || []).join(","),
        LayoutMode: layout === 2 || layout === 4 ? layout : 1,
        DisplayPreset: this.form.violationDisplayPreset || "day",
        DisplayDays:
          this.form.violationDisplayDays != null && Number(this.form.violationDisplayDays) >= 1
            ? Number(this.form.violationDisplayDays)
            : 1,
        DutyItemId: dutyId > 0 ? String(dutyId) : null,
        DutyItemInvalid: !!this.form.violationDutyItemInvalid
      };
    },
    flushCurrentCampusScope() {
      const cid = String(this.scopeCampusTab || "").trim();
      if (!cid || cid === "0") return;
      this.$set(this.campusScopeMap, cid, this.buildCampusScopeSnapshot());
    },
    applyCampusScopeToForm(campusId) {
      const cid = String(campusId || "").trim();
      const saved = cid && cid !== "0" ? this.campusScopeMap[cid] : null;
      const def = this.schoolDefaults || {};
      if (saved) {
        this.form.violationPublishAllScreens = !!saved.PublishAllScreens;
        this.form.violationScreenIdList = saved.ScreenIds
          ? String(saved.ScreenIds).split(",").map(x => x.trim()).filter(Boolean)
          : [];
        const layout = Number(saved.LayoutMode);
        this.form.violationLayoutMode =
          layout === 2 || layout === 4 ? layout : (layout === 1 ? 1 : (def.layoutMode || 1));
        this.form.violationDisplayPreset = saved.DisplayPreset || def.displayPreset || "day";
        this.form.violationDisplayDays =
          saved.DisplayDays != null && Number(saved.DisplayDays) >= 1
            ? Number(saved.DisplayDays)
            : (def.displayDays || 1);
        const dutyId = saved.DutyItemId > 0 ? String(saved.DutyItemId) : null;
        this.form.violationDutyItemId = dutyId;
        this.form.violationDutyItemInvalid = !!saved.DutyItemInvalid;
      } else {
        this.form.violationPublishAllScreens = true;
        this.form.violationScreenIdList = [];
        this.form.violationLayoutMode = def.layoutMode || 1;
        this.form.violationDisplayPreset = def.displayPreset || "day";
        this.form.violationDisplayDays = def.displayDays || 1;
        // 无校区草稿时，用学校级旧值日项回填一次
        this.form.violationDutyItemId = this.legacyDutyItemId > 0 ? String(this.legacyDutyItemId) : null;
        this.form.violationDutyItemInvalid = false;
      }
    },
    /** 切换校区 Tab 前先回写当前校区草稿，并加载目标校区值日树 */
    beforeScopeCampusLeave(activeName, oldActiveName) {
      const prev = String(oldActiveName || "").trim();
      if (prev && prev !== "0") {
        this.$set(this.campusScopeMap, prev, this.buildCampusScopeSnapshot());
      }
      this.applyCampusScopeToForm(activeName);
      this.loadDutyTreeForCampus(activeName);
      return true;
    },
    buildCampusScopesPayload() {
      this.flushCurrentCampusScope();
      const scopes = [];
      const def = this.schoolDefaults || {};
      Object.keys(this.campusScopeMap || {}).forEach(key => {
        const cid = String(key);
        const row = this.campusScopeMap[key];
        if (!cid || cid === "0" || !row) return;
        const all = !!row.PublishAllScreens;
        const layoutNum = Number(row.LayoutMode);
        const layout =
          layoutNum === 1 || layoutNum === 2 || layoutNum === 4
            ? layoutNum
            : (Number(def.layoutMode) === 2 || Number(def.layoutMode) === 4 ? Number(def.layoutMode) : 1);
        const preset = row.DisplayPreset || def.displayPreset || "day";
        const days =
          row.DisplayDays != null && Number(row.DisplayDays) >= 1
            ? Number(row.DisplayDays)
            : (def.displayDays >= 1 ? Number(def.displayDays) : 1);
        const dutyId = row.DutyItemId > 0 ? String(row.DutyItemId) : null;
        scopes.push({
          CampusId: cid,
          PublishAllScreens: all,
          ScreenIds: all ? "" : (row.ScreenIds || ""),
          LayoutMode: layout,
          DisplayPreset: preset,
          DisplayDays: days,
          DutyItemId: dutyId || 0
        });
      });
      // 若用户只编辑了当前校区且 map 为空以外的校区尚未进 map，至少保证当前 Tab 已 flush
      return scopes;
    },
    loadConfig() {
      this.loadTreesAndScreens();
      getDismissalScreenConfig({ departmentId: this.DepartmentId })
        .then(res => {
          if (res && res.data && res.data.success && res.data.response) {
            const r = res.data.response;
            // 后端 Newtonsoft 默认 PascalCase，需兼容两种命名
            const needApproval = r.needApproval != null ? r.needApproval : r.NeedApproval;
            this.form.needApproval = !!needApproval;
            const parentDingNotifyEnabled =
              r.parentDingNotifyEnabled != null ? r.parentDingNotifyEnabled : r.ParentDingNotifyEnabled;
            this.form.parentDingNotifyEnabled =
              parentDingNotifyEnabled === undefined || parentDingNotifyEnabled === null
                ? true
                : !!parentDingNotifyEnabled;
            const rc = r.VoiceRepeatCount != null ? r.VoiceRepeatCount : r.voiceRepeatCount;
            this.form.voiceRepeatCount = rc != null ? Number(rc) : 2;
            const suf = r.VoiceSuffix != null ? r.VoiceSuffix : r.voiceSuffix;
            this.form.voiceSuffix = suf != null ? suf : "正在放学，请家长在等候区等待";
            const ate = r.AutoTriggerEnabled != null ? r.AutoTriggerEnabled : r.autoTriggerEnabled;
            this.form.autoTriggerEnabled = !!ate;
            const att = r.AutoTriggerThreshold != null ? r.AutoTriggerThreshold : r.autoTriggerThreshold;
            this.form.autoTriggerThreshold = att != null ? Number(att) : 1;
            this.form.ledNotifyEnabled = !!this.pick(r, "ledNotifyEnabled", "LedNotifyEnabled");
            const ledInterval = this.pick(r, "ledVoiceIntervalSeconds", "LedVoiceIntervalSeconds");
            this.form.ledVoiceIntervalSeconds = ledInterval != null ? Number(ledInterval) : 5;
            const evalId = this.pick(r, "violationEvalItemId", "ViolationEvalItemId");
            this.form.violationEvalItemId = evalId > 0 ? String(evalId) : null;
            this.form.violationEvalItemInvalid = !!this.pick(r, "violationEvalItemInvalid", "ViolationEvalItemInvalid");
            this.form.violationEvalScoreMode = this.pick(r, "violationEvalScoreMode", "ViolationEvalScoreMode") || "";
            const itemScore = this.pick(r, "violationEvalItemScore", "ViolationEvalItemScore");
            this.form.violationEvalItemScore = itemScore != null ? Number(itemScore) : 0;
            const deductScore = this.pick(r, "violationEvalDeductScore", "ViolationEvalDeductScore");
            this.form.violationEvalDeductScore = deductScore != null && Number(deductScore) > 0 ? Number(deductScore) : null;
            const legacyDutyId = this.pick(r, "violationDutyItemId", "ViolationDutyItemId");
            this.legacyDutyItemId = legacyDutyId > 0 ? String(legacyDutyId) : null;
            const showScore = this.pick(r, "violationShowDeductScore", "ViolationShowDeductScore");
            this.form.violationShowDeductScore = showScore === undefined || showScore === null ? true : !!showScore;
            const scopesRaw = this.pick(r, "violationCampusScreenScopes", "ViolationCampusScreenScopes") || [];
            this.campusScopeMap = this.parseCampusScopesFromResponse(scopesRaw);
            // 旧学校级值日项：校区 scope 未带 DutyItemId 时回填，便于首次打开仍能看到。最后更新：2026-09-12
            if (this.legacyDutyItemId) {
              Object.keys(this.campusScopeMap).forEach(cid => {
                const row = this.campusScopeMap[cid];
                if (row && !(row.DutyItemId > 0)) {
                  row.DutyItemId = this.legacyDutyItemId;
                  row.DutyItemInvalid = !!this.pick(r, "violationDutyItemInvalid", "ViolationDutyItemInvalid");
                }
              });
            }
            const layout = this.pick(r, "violationLayoutMode", "ViolationLayoutMode");
            const schoolLayout = layout === 2 || layout === 4 ? Number(layout) : 1;
            const schoolPreset = this.pick(r, "violationDisplayPreset", "ViolationDisplayPreset") || "day";
            const days = this.pick(r, "violationDisplayDays", "ViolationDisplayDays");
            const schoolDays = days != null && Number(days) >= 1 ? Number(days) : 1;
            this.schoolDefaults = {
              layoutMode: schoolLayout,
              displayPreset: schoolPreset,
              displayDays: schoolDays
            };
            if (this.scopeCampusTab) {
              this.applyCampusScopeToForm(this.scopeCampusTab);
              this.loadDutyTreeForCampus(this.scopeCampusTab);
            } else if (this.campusOptions.length) {
              this.scopeCampusTab = String(this.campusOptions[0].Id);
              this.applyCampusScopeToForm(this.scopeCampusTab);
              this.loadDutyTreeForCampus(this.scopeCampusTab);
            } else {
              this.form.violationPublishAllScreens = true;
              this.form.violationScreenIdList = [];
              this.form.violationLayoutMode = schoolLayout;
              this.form.violationDisplayPreset = schoolPreset;
              this.form.violationDisplayDays = schoolDays;
              this.form.violationDutyItemId = this.legacyDutyItemId;
              this.form.violationDutyItemInvalid = !!this.pick(r, "violationDutyItemInvalid", "ViolationDutyItemInvalid");
            }
          } else {
            this.form.needApproval = false;
            this.form.parentDingNotifyEnabled = true;
            this.form.voiceRepeatCount = 2;
            this.form.voiceSuffix = "正在放学，请家长在等候区等待";
            this.form.autoTriggerEnabled = false;
            this.form.autoTriggerThreshold = 1;
            this.form.ledNotifyEnabled = false;
            this.form.ledVoiceIntervalSeconds = 5;
            this.resetViolationForm();
          }
        })
        .catch(() => {
          this.$message.error("加载配置失败");
          this.form.needApproval = false;
          this.form.parentDingNotifyEnabled = true;
          this.form.voiceRepeatCount = 2;
          this.form.voiceSuffix = "正在放学，请家长在等候区等待";
          this.form.autoTriggerEnabled = false;
          this.form.autoTriggerThreshold = 1;
          this.form.ledNotifyEnabled = false;
          this.form.ledVoiceIntervalSeconds = 5;
          this.resetViolationForm();
        })
        .finally(() => {
          this.loading = false;
        });
    },
    saveConfig() {
      if (!this.DepartmentId) {
        this.$message.warning("请先选择学校/机构");
        return;
      }
      this.flushCurrentCampusScope();
      const scopes = this.buildCampusScopesPayload();
      for (let i = 0; i < scopes.length; i++) {
        const s = scopes[i];
        const name = (this.campusOptions.find(c => String(c.Id) === String(s.CampusId)) || {}).Name || s.CampusId;
        if (!s.PublishAllScreens && (!s.ScreenIds || !String(s.ScreenIds).trim())) {
          this.$message.warning(`校区「${name}」指定大屏时请至少选择 1 块`);
          return;
        }
        if (s.DisplayPreset === "custom" && (!s.DisplayDays || s.DisplayDays < 1)) {
          this.$message.warning(`校区「${name}」请填写自定义展示天数`);
          return;
        }
      }
      if (this.evalScoreIsMax) {
        const score = Number(this.form.violationEvalDeductScore);
        if (!score || score <= 0 || score > this.evalItemScoreMax) {
          this.$message.warning("点评扣分项为上限模式，请填写不超过上限的实际扣分");
          return;
        }
      }
      this.saving = true;
      setDismissalScreenConfig({
        DepartmentId: this.DepartmentId,
        NeedApproval: this.form.needApproval,
        ParentDingNotifyEnabled: this.form.parentDingNotifyEnabled,
        VoiceRepeatCount: this.form.voiceRepeatCount,
        VoiceSuffix: this.form.voiceSuffix || "正在放学，请家长在等候区等待",
        AutoTriggerEnabled: this.form.autoTriggerEnabled,
        AutoTriggerThreshold: this.form.autoTriggerThreshold,
        LedNotifyEnabled: this.form.ledNotifyEnabled,
        LedVoiceIntervalSeconds: this.form.ledVoiceIntervalSeconds,
        ViolationEvalItemId: this.form.violationEvalItemId || 0,
        ViolationEvalDeductScore: this.evalScoreIsMax ? this.form.violationEvalDeductScore : null,
        ViolationShowDeductScore: this.form.violationShowDeductScore,
        ViolationCampusScreenScopes: scopes
      })
        .then(res => {
          this.saving = false;
          if (res && res.data && res.data.success) {
            this.$message.success("保存成功");
            // 回读，确保各校区投放草稿与库一致
            this.loadConfig();
          } else {
            this.$message.error((res && res.data && res.data.msg) || "保存失败");
          }
        })
        .catch(() => {
          this.saving = false;
          this.$message.error("保存失败，请稍后重试");
        });
    },
    callFunction() {}
  },
  mounted() {
    let routers = window.localStorage.router ? JSON.parse(window.localStorage.router) : [];
    this.buttonList = getButtonList(this.$route.path, routers);
  }
};
</script>

<style scoped>
.dsc-page {
  --dsc-text: #303133;
  --dsc-muted: #606266;
  --dsc-line: #ebeef5;
  --dsc-primary: var(--btn-primary, #2563eb);
}

.dsc-page .dp-right {
  padding: 12px 20px 16px !important;
  box-sizing: border-box;
}

.dsc-shell {
  max-width: 1180px;
  width: 100%;
  margin: 0 auto;
  padding: 4px 8px 12px;
  box-sizing: border-box;
}

.dsc-body {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.dsc-hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  padding: 0 2px;
}

.dsc-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--dsc-text);
}

.dsc-config-tabs >>> .el-tabs__header {
  margin: 0 0 12px;
}

.dsc-config-tabs >>> .el-tabs__item {
  font-size: 14px;
  height: 36px;
  line-height: 36px;
}

.dsc-config-tabs >>> .el-tabs__content {
  overflow: visible;
}

.dsc-page .dsc-config-tabs.el-tabs {
  flex: none !important;
  height: auto !important;
  min-height: 0 !important;
  overflow: visible !important;
}

.dsc-page .dsc-config-tabs >>> .el-tab-pane {
  height: auto !important;
  overflow: visible !important;
}

.dsc-tiles {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  align-items: stretch;
}

.dsc-tiles--violation .dsc-tile--full {
  grid-column: 1 / -1;
}

.dsc-tile {
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: 16px 18px 14px;
  background: #fff;
  border: 1px solid var(--dsc-line);
  border-radius: 8px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  box-sizing: border-box;
}

.dsc-tile:hover {
  border-color: #d4e3fc;
  box-shadow: 0 4px 14px rgba(64, 158, 255, 0.08);
}

.dsc-tile__title {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 12px;
  font-size: 14px;
  font-weight: 600;
  color: var(--dsc-text);
}

.dsc-tile__title i {
  color: var(--dsc-primary);
  font-size: 16px;
}

.dsc-tile__body {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  flex: 1;
}

.dsc-tile__body--stack {
  flex-direction: column;
  align-items: stretch;
  gap: 10px;
}

.dsc-k {
  flex-shrink: 0;
  font-size: 13px;
  color: var(--dsc-muted);
}

.dsc-led-timing {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  padding: 8px 10px;
  font-size: 12px;
  line-height: 1.6;
  color: #475569;
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
}
.dsc-led-timing i {
  margin-top: 3px;
  color: #3b82f6;
}
.dsc-led-timing b {
  color: #2563eb;
  font-weight: 600;
}
.dsc-desc {
  margin: 12px 0 0;
  padding-top: 10px;
  border-top: 1px dashed var(--dsc-line);
  font-size: 12px;
  line-height: 1.6;
  color: #909399;
}

.dsc-warn {
  margin: 6px 0 0;
  font-size: 12px;
  color: #e6a23c;
}

.dsc-inline {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.dsc-unit {
  font-size: 13px;
  color: var(--dsc-muted);
}

.dsc-voice {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.dsc-voice__prefix {
  flex-shrink: 0;
  font-size: 12px;
  color: #909399;
  background: #f4f4f5;
  border-radius: 4px;
  padding: 4px 8px;
}

.dsc-voice >>> .el-input {
  flex: 1;
  min-width: 0;
}

.dsc-full {
  width: 100%;
}

.dsc-field-block {
  min-width: 0;
}

.dsc-field-block .dsc-k {
  display: block;
  margin-bottom: 6px;
}

.dsc-score {
  margin-top: 6px;
}

.dsc-select-mt {
  margin-top: 8px;
}

.dsc-campus-tabs {
  width: 100%;
}

.dsc-campus-tabs >>> .el-tabs__header {
  margin: 0 0 4px;
}

.dsc-campus-tabs >>> .el-tabs__nav-wrap::after {
  display: none;
}

.dsc-campus-tabs >>> .el-tabs__item {
  height: 32px;
  line-height: 32px;
  font-size: 13px;
}

.dsc-campus-tabs >>> .el-tabs__content {
  display: none;
}

.dsc-tile >>> .el-radio-group {
  display: inline-flex;
}

.dsc-state {
  min-height: 360px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #909399;
  gap: 10px;
  padding: 24px 16px;
  box-sizing: border-box;
}

.dsc-state__icon {
  font-size: 42px;
  color: #dcdfe6;
}

.dsc-state__icon.is-spin {
  color: var(--dsc-primary);
  animation: dsc-spin 0.9s linear infinite;
}

@keyframes dsc-spin {
  to { transform: rotate(360deg); }
}

@media (prefers-reduced-motion: reduce) {
  .dsc-tile,
  .dsc-state__icon.is-spin {
    transition: none;
    animation: none;
  }
}

@media (max-width: 960px) {
  .dsc-page .dp-right {
    padding: 10px 14px 14px !important;
  }

  .dsc-shell {
    padding: 2px 4px 8px;
  }

  .dsc-tiles {
    grid-template-columns: 1fr;
  }
}
</style>
